(ns capra.context
  "Pipeline recording context for pipe!.

   Inside pipe!, each call to capra/call records an op in the context
   and returns a PendingRef. The PendingRef becomes #rpc/ref on the wire."
  (:require
   [capra.protocol :as proto]
   [capra.stub :as stub]
   [clojure.walk :as walk]))

;; =============================================================================
;; PendingRef — returned by call inside pipe!
;; =============================================================================

(defrecord PendingRef [op-id])

(defn pending-ref? [x]
  (instance? PendingRef x))

(defn ref-path
  "Create a PendingRef with a get-in path.
   (ref-path some-ref :quotes) → #rpc/ref [N :quotes] on the wire."
  [pending-ref & path]
  (proto/->Ref (:op-id pending-ref) (vec path)))

;; =============================================================================
;; Callback Reference — for #rpc/fn in pipeline args
;; =============================================================================

(defrecord CapraCallback [fn-id local-fn])

(defn callback? [x]
  (instance? CapraCallback x))

;; =============================================================================
;; Arg Normalization
;; =============================================================================

(defn- normalize-val
  "Normalize a single value — replace capra types with wire forms."
  [x]
  (cond
    (pending-ref? x)               (proto/->Ref (:op-id x) nil)
    (stub/stub? x)                 (stub/stub->wire x)
    (callback? x)                  (proto/->FnRef (:fn-id x))
    ;; Duck-type fallback: anything with :fn-id and :local-fn is a callback
    (and (map? x) (:fn-id x) (:local-fn x)) (proto/->FnRef (:fn-id x))
    (proto/ref? x)                 x
    (map? x)                       (persistent!
                                    (reduce-kv (fn [m k v] (assoc! m k (normalize-val v)))
                                               (transient {}) x))
    (vector? x)                    (mapv normalize-val x)
    (sequential? x)                (map normalize-val x)
    :else                          x))

(defn normalize-args
  "Walk args, replacing PendingRef/CapraStub/CapraCallback with wire forms."
  [args]
  (normalize-val args))

;; =============================================================================
;; PipelineCtx
;; =============================================================================

(defrecord PipelineCtx [ops-atom next-id-atom])

(defn pipeline-ctx? [x]
  (instance? PipelineCtx x))

(defn make-pipeline-ctx []
  (->PipelineCtx (atom []) (atom -1)))

(defn record-op!
  "Record an op in the pipeline context. Returns a PendingRef."
  [ctx target method args]
  (let [op-id   (swap! (:next-id-atom ctx) inc)
        wire-target (cond
                      (nil? target)          nil
                      (= :root target)      nil
                      (stub/stub? target)    (stub/stub->wire target)
                      (pending-ref? target)  (proto/->Ref (:op-id target) nil)
                      ;; Ref with path (from ref-path)
                      (proto/ref? target)    target
                      :else (throw (ex-info "Invalid call target"
                                            {:target target})))
        wire-args (normalize-args args)]
    (swap! (:ops-atom ctx) conj
           (cond-> {:op method :args wire-args}
             wire-target (assoc :cap wire-target)))
    (->PendingRef op-id)))
